import './index.scss';
import React, {FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {ReduxActions, useAppDispatch, useAppSelector} from "../../redux";
import {Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Fade, IconButton, Skeleton, Theme, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {FinalForm, Form, MuiRFF, SaveOrRollback, useIsMounted, Yup} from "../../../@bizkeytech";
import classNames from "classnames";
import {DeleteOutlined, EditOutlined} from "@mui/icons-material";
import type {ApiServiceModels} from "../../../core/services/api";
import ApiService from "../../../core/services/api";
import Texts from "../../../core/models/texts";

interface Entry extends ApiServiceModels.ListOrganizationGroupResponseDs {
    inEditMode: boolean,
    editing: boolean,
}

const ManageOrganizationGroupsDialog: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(state => state.dialogs?.manageOrganizationGroups);
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Array<Entry>>([]);
    const isMounted = useIsMounted();

    const {hideCloseButton, disableBackdropClick, ...dialogProps} = state;

    const editing = useMemo(() => data.some(e => e.editing), [data]);

    /**
     * Resets the state of the dialog when it is closed.
     */
    useLayoutEffect(() => {
        if (state.open) {
            setData([]);
            setLoading(false);
            setCreating(false);
        }
    }, [state.open]);

    /**
     * Fetches the entries to be managed in this dialog.
     */
    const getEntries = useCallback(async (abortController: AbortController) => {
        setLoading(true);
        const response = await ApiService.v1.organizationGroup.list(abortController);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setData(response.data?.map(e => ({
                ...e,
                inEditMode: false,
                editing: false,
            })) ?? []);
        }
    }, [isMounted]);

    /**
     * As soon as the dialog opens:
     * - fetches the list of origins of leads
     */
    useEffect(() => {
        if (!state?.open)
            return;
        const abortController = new AbortController();
        getEntries(abortController).then();
        return () => abortController.abort();
    }, [getEntries, state?.open]);

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback((successful = false) => {
        if (state?.onClose) {
            state.onClose(successful ? "successful" : "leave", loading ? null : {entries: data});
        }
        dispatch(ReduxActions.dialogs.manageOrganizationGroups({open: false}));
    }, [state, dispatch, loading, data])

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback((event: Object, reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick') => {
        switch (reason) {
            case "backdropClick":
                if (!state.disableBackdropClick && !creating && !editing)
                    closeDialog();
                break;
            case "closeClick":
                if (!state.hideCloseButton && !creating && !editing)
                    closeDialog();
                break;
            case "escapeKeyDown":
                if (!state.disableEscapeKeyDown && !creating && !editing)
                    closeDialog();
                break;
            default:
                break;
        }
    }, [closeDialog, creating, editing, state.disableBackdropClick, state.disableEscapeKeyDown, state.hideCloseButton])


    const schema = useMemo(() => Yup.object({
        title: Yup.string()
            .required(Texts.FormValidation.requiredField)
            .trim(),
    }), []);

    interface Schema extends Yup.InferType<typeof schema> {
    }

    type SchemaKeys = keyof Schema;

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo<Record<SchemaKeys, boolean>>(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Submits the form values to create a entry in the system.
     * @param {Schema} values the values of the form
     * @param {FinalForm.FormApi<Schema, Partial<Schema>>} form the form api
     * @return {Promise<void>}
     */
    const createOrigin = useCallback<FinalForm.Config<Schema>["onSubmit"]>(async (values, form) => {
        setCreating(true);
        const response = await ApiService.v1.organizationGroup.create({name: values.title});
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag && !response.aborted) {
            setData(prevState => [
                ...prevState,
                {
                    ...response.data!,
                    inEditMode: false,
                    editing: false
                }
            ]);
            form.change('title', '')
            form.resetFieldState('title');
        }
    }, [isMounted, setData]);

    /**
     * Updates the model of the entry in the system.
     *
     * @param {Entry} entry the entry to be updated
     * @return {Promise<boolean>}
     */
    const updateOrigin = useCallback(async (entry: Entry) => {
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: true} : e));
        const response = await ApiService.v1.organizationGroup.update({
            id: entry.id,
            name: entry.name,
        })
        if (!isMounted())
            return false
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: false} : e));
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === entry.id ? ({
                ...e,
                name: entry.name,
                inEditMode: false,
            }) : e) ?? []);
        }
        return response?.resultFlag ?? false
    }, [isMounted, setData])

    /**
     * Removes an entry from the system.
     * @param {Entry} entry the entry to be removed.
     */
    const removeOrigin = useCallback(async (entry: Entry) => {
        dispatch(ReduxActions.dialogs.removeConfirmation({
            open: true,
            data: {
                data: data.filter(e => e.id !== entry.id).map(e => ({title: e.name, id: e.id, data: e})),
                replacementOptional: true,
                removedEntryTitle: `The selected group: ${entry.name ?? ""}`
            },
            onClose: async (reason, dialogData) => {
                if (reason !== 'successful')
                    return false;
                const replaceWith = dialogData as Entry;

                setData(prevState => prevState.map(e =>
                    e.id === entry.id
                        ? {...e, editing: true}
                        : e
                ));
                const response = await ApiService.v1.organizationGroup.delete({
                    id: entry.id,
                    replacementId: replaceWith?.id,
                })
                if (!isMounted())
                    return false
                if (response?.resultFlag) {
                    setData(prevState => prevState.filter(e => e.id !== entry.id));
                } else {
                    setData(prevState => prevState.map(e => e.id === entry.id ? ({...e, editing: false}) : e));
                }
                return response?.resultFlag ?? false
            }
        }))
    }, [data, dispatch, isMounted, setData]);

    /**
     * Toggles the edit mode of the origin.
     */
    const toggleInEditMode = useCallback((origin: Entry, inEditMode: boolean = false) => {
        setData(prevState => prevState.map(e => e.id === origin.id ? {...e, inEditMode} : e))
    }, [setData])

    /**
     * Creates the styles for the container boxes.
     * @param {Theme} theme                      The theme to use
     * @returns {ReturnType<ThemeMixin<Theme>>}  The styles for the container boxes
     */
    const boxMixin = useCallback<ThemeMixin<Theme>>((theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        borderRadius: theme.vars?.shape.borderRadius,
        padding: 2,
        marginBlock: 2,
    }), []);

    return (
        <>
            <Dialog
                {...dialogProps}
                onClose={onDialogClosed}
                className={'manage-employee-groups-dialog'}
            >
                <DialogTitle fontWeight={600}>
                    Manage Lead Origins
                </DialogTitle>
                <DialogContent>
                    <Form<Schema, Partial<Schema>>
                        onSubmit={createOrigin}
                        validate={validate}
                    >
                        <Box sx={boxMixin}>
                            <Typography variant={'subtitle2'} fontWeight={600}>
                                Create a new Origin
                            </Typography>
                            <Grid container xs={12} gap={2} spacing={2} marginInline={0} marginBlock={2}>
                                <Grid xs padding={0}>
                                    <MuiRFF.TextField
                                        fullWidth
                                        name={"title"}
                                        label={"Title"}
                                        required={required.title}
                                    />
                                </Grid>
                                <Grid xs={'auto'} padding={0}>
                                    <Button
                                        variant={'contained'}
                                        type={'submit'}
                                        color={'primary'}
                                        size={'medium'}
                                        disabled={creating}
                                    >
                                        {
                                            creating
                                                ? "Creating..."
                                                : "Create"
                                        }
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Form>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        {
                            data?.map((origin, index, array) => (
                                <ManageLeadOriginsDialogOriginCard
                                    key={origin.id}
                                    origin={origin}
                                    update={updateOrigin}
                                    remove={removeOrigin}
                                    removable={array.length > 1}
                                    setInEditMode={toggleInEditMode}
                                />
                            ))
                        }
                    </Box>
                    {
                        loading &&
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2,}}>
                            {
                                Array.from({length: 4}).map((_, key) => (
                                    <Skeleton
                                        key={key}
                                        width={'100%'}
                                        sx={{minHeight: 75}}
                                    />
                                ))
                            }
                        </Box>
                    }
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Button
                        variant={'text'}
                        type={'button'}
                        color={'primary'}
                        onClick={() => onDialogClosed({}, 'closeClick')}
                        disabled={editing}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}


interface ManageLeadOriginsDialogOriginCardProps {
    origin: Entry,
    update: (origin: Entry) => Promise<boolean>,
    remove: (origin: Entry) => Promise<void>,
    removable: boolean,
    setInEditMode: (origin: Entry, inEditMode?: boolean) => void,
}

const ManageLeadOriginsDialogOriginCard: FunctionComponent<ManageLeadOriginsDialogOriginCardProps> = (props) => {
    const {update, remove, setInEditMode} = props;

    const schema = useMemo(() => Yup.object({
        title: Yup.string()
            .required(Texts.FormValidation.requiredField)
            .trim(),
    }), []);

    interface Schema extends Yup.InferType<typeof schema> {
    }

    type SchemaKeys = keyof Schema;

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo<Record<SchemaKeys, boolean>>(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo<Partial<Schema>>(() => ({
        title: props.origin.name,
    }), [props.origin.name])

    /**
     * Updates the origin by calling the appropriate callback in the props.
     */
    const updateOrigin = useCallback<FinalForm.Config<Schema>["onSubmit"]>(async (values) => {
        if (props.origin.name === values.title) {
            return setInEditMode(props.origin, false);
        }
        await update({...props.origin, name: values.title,});
    }, [props.origin, update, setInEditMode])

    /**
     * Removes the origin by calling the appropriate callback in the props.
     */
    const removeOrigin = useCallback(async () => {
        await remove(props.origin)
    }, [remove, props.origin])

    /**
     * Set the origin as in edit mode.
     */
    const setAsEditing = useCallback(() => {
        setInEditMode(props.origin, true);
    }, [setInEditMode, props.origin])

    /**
     * Rolls back the values of the form to its initial state.
     */
    const rollbackValues = useCallback((form: FinalForm.FormApi<Schema, Partial<Schema>>) => {
        setInEditMode(props.origin, false);
        form.reset(initialValues);
    }, [initialValues, props.origin, setInEditMode]);

    return (
        <>
            <Card variant={'outlined'} sx={{paddingInline: 2}}>
                <Form<Schema, Partial<Schema>>
                    onSubmit={updateOrigin}
                    validate={validate}
                    initialValues={initialValues}
                >
                    {({form}) => (
                        <Grid container xs={12} gap={1} spacing={2} marginInline={0} marginBlock={2}>
                            <Grid xs padding={0}>
                                <MuiRFF.TextField
                                    fullWidth
                                    name={"title"}
                                    label={"Title"}
                                    required={required.title}
                                    className={classNames({'show-as-text': !props.origin.inEditMode})}
                                />
                            </Grid>
                            <Grid xs={'auto'} padding={0}>
                                {
                                    !props.origin.inEditMode &&
                                    <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5}}>
                                        <Fade in={props.removable}>
                                            <IconButton disabled={!props.removable} onClick={removeOrigin} color={'paper.error'}>
                                                <DeleteOutlined/>
                                            </IconButton>
                                        </Fade>
                                        <IconButton onClick={setAsEditing} color={'paper.primary'}>
                                            <EditOutlined/>
                                        </IconButton>
                                    </Box>
                                }
                                {
                                    props.origin.inEditMode &&
                                    <SaveOrRollback
                                        layout={'row'}
                                        onSave={() => form.submit()}
                                        onRollback={() => rollbackValues(form)}
                                        loading={props.origin.editing}
                                        sx={{height: '100%'}}
                                    />
                                }
                            </Grid>
                        </Grid>
                    )}
                </Form>
            </Card>
        </>
    );
}

export default ManageOrganizationGroupsDialog;
